import { Button, Subtitle1, Subtitle2, Table, TableBody, TableCell, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Text, Title1, Tooltip } from '@fluentui/react-components';
import { DocumentFilled, Pin12Filled, Pin12Regular, SettingsRegular } from '@fluentui/react-icons';
import React, { FC, useEffect, useState } from 'react';
import TenderingSettingsModal from './TenderingSettings/TenderingSettingsModal';
import authService from '../services/authService';
import files from './filePaths';
import { getNewsByQuery } from '../services/newsService';
import { getTenderDocumentsByTenderingID, getTenderingDocument } from '../services/tenderingService';
import { Module } from '../models/module';
import { News, NewsListItem } from '../models/news';
import { Tender, Tendering } from '../models/tendering';
import CreatePlacement from './Placement/PlacementDialogs/CreatePlacement';
import NewsDialog from './dialogs/NewsDialog';
import { useAppContext } from '../AppContext';
import { RoleDTO } from '../models/role';

interface TenderingProps {
  tenderingModule: Module
}


const TenderingComponent: FC<TenderingProps> = ({tenderingModule}) => {
  const [showSettings, setShowSetting] = useState(false);
  const [showCreatePlacement, setShowCreatePlacement] = useState(false);
  const [tenderingFiles, setTenderingFiles] = useState<any[]>([]);
  const [news, setNews] = useState<NewsListItem[]>([]);
  const [tenderingDocument, setTenderingDocument] = useState<Tendering>();
  const [tenders, setTenders] = useState<Tender[]>([])
  const [selectedNews, setSelectedNews] = useState<NewsListItem | null>(null);
  const [newsDialogOpen, setNewsDialogOpen] = useState<boolean>(false);

  const {user, getFeaturePermissionsByModuleID, hasTenderingPageAccess} = useAppContext();
  const featurePermissions = getFeaturePermissionsByModuleID(tenderingModule.moduleID);

  useEffect(() => {
    if(!hasTenderingPageAccess(tenderingModule.moduleID)) return;
    setTenderingFiles(user.roles.some((r: RoleDTO)=> r.roleType == 'Supplier') ? files.filter(f => f.tenderingID == tenderingModule.moduleID && !f.hideFromSupplier) : files.filter(f => f.tenderingID == tenderingModule.moduleID));
    fetchNews();
    fetchDocument();
    if(user.roles.some((r: RoleDTO) => r.roleType == 'General')){
      fetchTenderDocuments();
    }
  }, [tenderingModule])

  const fetchNews = async () => {
    try {
      const result = await getNewsByQuery({
        ModuleID: tenderingModule.moduleID,
        newsTypeID: 3,
        pageIndex: 1,
        pageSize: 10
      });
      setNews(result.news);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    }
  };

  const fetchDocument = async () => {
    const id = tenderingModule.moduleID;
    setTenderingDocument(undefined);

    try {
      const response = await getTenderingDocument(id, ["content"]);
      setTenderingDocument(response);
    } catch (error) {
      console.error('Error fetching document:', error);
    }
  };

  const fetchTenderDocuments = async () => {
    const id = tenderingModule.moduleID;
    const properties  = ["content.indexing", "content.tenderAnswers", "content.tenderingParts"]

    try{
      const response = await getTenderDocumentsByTenderingID(id, properties);
      setTenders(response);
    } catch(error){
      console.error('Error fetching tender documents:', error);
    }
  }

  const toggleSettingsDialog = () => {
    setShowSetting(!showSettings);
  };

  const toggleCreatePlacementDialog = () => {
    setShowCreatePlacement(!showCreatePlacement);
  }

  if (!hasTenderingPageAccess(tenderingModule.moduleID)) {
    return (
      <div className="flex justify-center items-center h-64">
        <Text size={500}>Du har inte behörighet att visa den här ramavtalet.</Text>
      </div>
    );
  }

  return (
    <>
    <div className="bg-[#ebebeb]">
      <div className="flex justify-center min-h-[350px] bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b w-full text-white px-7 pt-3 pb-20">
        <div className="w-full max-w-2xl mt-8 flex flex-col">
          <div className='flex mb-2 items-center'>
            <Title1>{tenderingModule.moduleName}</Title1>
            {featurePermissions?.some(p => p.action.includes('Tendering settings')) && (
              <Tooltip content="Upphandlingsinställningar" relationship='label'>
                <Button onClick={toggleSettingsDialog} size="small" appearance="subtle" icon={<SettingsRegular/>} disabled={!tenderingDocument}></Button>
              </Tooltip>
            )}
          </div>
          <div 
            className="text-sm mt-2 w-full break-words text-white [&_*]:!text-white [&_a]:!underline [&_a]:!font-bold [&_a]:!text-white hover:[&_a]:!text-stone-200"
            dangerouslySetInnerHTML={{ __html: tenderingDocument?.properties?.tenderingDescription  || '' }}
          />
          <div className=' gap-2 mt-5'>
            {featurePermissions?.some(fp => fp.action == 'Tendering part/Search placement') && tenderingDocument?.properties.tenderingSearchNameText && (
              <Button onClick={toggleCreatePlacementDialog} disabled={!tenderingDocument} size="large" appearance="primary" className="shrink-0 whitespace-nowrap">
                {tenderingDocument?.properties.tenderingSearchNameText}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-0 lg:-mt-10 flex flex-col lg:flex-row justify-center gap-10 px-4 max-w-[1500px] mx-auto relative z-10">
        <div className="bg-white rounded shadow-md h-[400px] overflow-y-auto p-3">
            <Table arial-label="Default table">
              <TableHeader style={{ height: "50px" }}>
                <TableRow>
                    <TableHeaderCell>
                        <Subtitle1>Nyheter</Subtitle1>
                    </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {news.length > 0 && news.map(n => (
                  <TableRow className='cursor-pointer' onClick={() => {setSelectedNews(n); setNewsDialogOpen(true)}}>
                    <TableCell>
                      <TableCellLayout>
                        <div className="flex w-[430px] items-center justify-between">
                            <div className='flex flex-col'>
                                <Text weight='bold'>{n.header}</Text>
                                <Text>{n.subject}</Text>
                            </div>
                            {n.highlighted && <div className='flex'>
                                <Pin12Regular />
                            </div>}
                        </div>
                      </TableCellLayout>
                    </TableCell>
                  </TableRow>
                  ))
                }
              </TableBody>
            </Table>
        </div>
        <div className="bg-white rounded shadow-md h-[400px] overflow-y-auto p-3">
            <Table arial-label="Default table">
              <TableHeader style={{ height: "50px" }}>
                <TableRow>
                    <TableHeaderCell>
                        <Subtitle1>Dokument</Subtitle1>
                    </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                  {tenderingFiles.length > 0 
                  ? 
                  tenderingFiles.map(tf => (
                  <TableRow key={tf.fileName}>
                    <TableCell>
                      <TableCellLayout>
                        <div className="flex w-[430px] items-center gap-2">
                            <div className='flex'>
                                <DocumentFilled />
                            </div>
                            <div className='flex flex-col'>
                                <a href={tf.url}>{tf.fileName}</a>
                            </div>
                        </div>
                      </TableCellLayout>
                    </TableCell>
                  </TableRow>)) 
                  : 
                  <Subtitle2>
                    Inga dokument hittas...
                  </Subtitle2>
                  }
              </TableBody>
            </Table>
          </div>
        </div>  
      </div>
      {tenderingDocument && <TenderingSettingsModal tenders={tenders} tenderingDocument={tenderingDocument} open={showSettings} onClose={toggleSettingsDialog} onSave={fetchDocument}/> }
      {tenderingDocument && <CreatePlacement tenderingDocument={tenderingDocument} open={showCreatePlacement} onClose={toggleCreatePlacementDialog}/> }
      {selectedNews && <NewsDialog isOpen={newsDialogOpen} onClose={() => setNewsDialogOpen(false)} news={selectedNews}/>}
    </>
  );
};

export default TenderingComponent;